import 'core-js'
import React, { Suspense, useEffect } from 'react'
import { createRoot } from 'react-dom/client'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import {
  Routes,
  Route,
  BrowserRouter,
  useLocation,
  useNavigationType,
  createRoutesFromChildren,
  matchRoutes,
} from 'react-router'
import { AuthProvider } from 'react-oidc-context'
import { WebStorageStateStore } from 'oidc-client-ts'
import * as Sentry from '@sentry/react'
import './i18n'

// FPCC
import 'assets/main.css'
import App from 'components/App'
import Loading from 'components/Loading'
import ScrollToTopOnMount from 'common/ScrollToTopOnMount'
import GlobalConfiguration from 'src/GlobalConfiguration'
import { SiteProvider } from 'context/SiteContext'
import { UserProvider } from 'context/UserContext'
import { ORIGINAL_DESTINATION } from 'common/constants'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, { message: status }) => {
        if (status !== '404' && status !== '401') {
          return false
        }
        return failureCount > 2
      },
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    },
  },
})

const oidcConfig = {
  authority: GlobalConfiguration.OIDC_AUTHORITY_URL,
  client_id: GlobalConfiguration.AWS_CLIENT_ID,
  redirect_uri: GlobalConfiguration.OAUTH2_REDIRECT_URL,
  userStore: new WebStorageStateStore({ store: window.localStorage }),
  automaticSilentRenew: true,
  onSigninCallback: () => {
    // redirect to original location
    const url = window.sessionStorage.getItem(ORIGINAL_DESTINATION)
    if (url) {
      window.sessionStorage.removeItem(ORIGINAL_DESTINATION)
      window.location.replace(url)
    } else {
      // remove url params to complete the login
      window.history.replaceState({}, document.title, window.location.pathname)
      window.location.reload()
    }
  },
}

// Sentry Config
Sentry.init({
  dsn: GlobalConfiguration.SENTRY_DSN,
  environment: GlobalConfiguration.SENTRY_ENVIRONMENT,
  release: GlobalConfiguration.SENTRY_RELEASE,
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.reactRouterV7BrowserTracingIntegration({
      useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes,
    }),
  ],
  // Set tracesSampleRate to 1.0 to capture 100% of transactions for performance monitoring.
  tracesSampleRate: parseFloat(GlobalConfiguration.SENTRY_TRACES_SAMPLE_RATE),
  // Capture Replay for 10% of all sessions
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: parseFloat(
    GlobalConfiguration.SENTRY_ERROR_SAMPLE_RATE,
  ),
})

const SentryRoutes = Sentry.withSentryReactRouterV7Routing(Routes)

// Dynamic font injection
const fontUrl = GlobalConfiguration.FONT_FAMILY_URL
if (fontUrl) {
  const style = document.createElement('style')
  style.innerHTML = `
    @font-face {
      font-family: 'Dynamic Font';
      font-style: normal;
      font-weight: 100 1000;
      font-display: swap;
      src: url('${fontUrl}') format('truetype');
    }

    :root {
      --font-sans: 'Dynamic Font', 'BCSans', sans-serif;
    }
  `
  document.head.appendChild(style)
}

const container = document.getElementById('root')
const root = createRoot(container)

root.render(
  <QueryClientProvider client={queryClient}>
    <AuthProvider {...oidcConfig}>
      <UserProvider>
        <SiteProvider>
          <BrowserRouter>
            <ScrollToTopOnMount />
            <Suspense fallback={<Loading.Container isLoading />}>
              <SentryRoutes>
                <Route path="*" element={<App.Container />} />
              </SentryRoutes>
            </Suspense>
          </BrowserRouter>
        </SiteProvider>
      </UserProvider>
    </AuthProvider>
  </QueryClientProvider>,
)
